// UpgradeLock — the shared, minimal "this isn't on your plan" affordance
// (PE-1625). Deliberately simple and consistent; polished locked-state visuals
// are Milestone 8, explicitly out of scope here.
//
// Two variants:
//   - inline (default): a compact locked notice to drop in place of (or beside)
//     a gated control — e.g. the contact-merge action, the EoY export.
//   - page: a centered full-page state for a route guard (EntitlementGate),
//     shown when a non-entitled org navigates directly to a gated route.
//
// The unlocking tier ("Available on Base") is DERIVED from the engine grid via
// unlockTierFor — never hard-coded — so it tracks the catalog automatically.
// The CTA routes to the Billing & Plans page (PE-1572). The server remains the
// authority; this is proactive UX only.

import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { Lock } from 'lucide-react';
import { unlockTierLabel } from '../../utils/entitlements';
import { trackMarketing, ENTITLEMENT_EVENTS } from '../../utils/marketingAnalytics';

const BILLING_PATH = '/settings/billing';

export function UpgradeLock({
  feature,
  variant = 'inline',
  title,
  description,
  className = '',
}: {
  // The entitlement feature key this surface gates on (e.g. 'contact_merging').
  feature: string;
  variant?: 'inline' | 'page';
  // Optional copy overrides; sensible defaults derive from the unlocking tier.
  title?: string;
  description?: string;
  className?: string;
}) {
  const history = useHistory();
  const posthog = usePostHog();
  const tierLabel = unlockTierLabel(feature);
  const heading = title ?? `Available on ${tierLabel}`;
  const body =
    description ?? `This feature isn't on your current plan. Upgrade to ${tierLabel} to unlock it.`;

  // entitlement_lock_viewed — a gated feature was surfaced to a non-entitled org.
  // Keyed on `feature` so any lock (contact-merge, EoY export, elements, …) is
  // rankable by which features most drive upgrade interest. Fires once per mount.
  const lockViewedRef = useRef(false);
  useEffect(() => {
    if (lockViewedRef.current || !posthog) return;
    lockViewedRef.current = true;
    trackMarketing(posthog, ENTITLEMENT_EVENTS.LOCK_VIEWED, {
      feature,
      unlock_tier: tierLabel,
      variant,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feature, posthog]);

  const goToBilling = () => {
    trackMarketing(posthog, ENTITLEMENT_EVENTS.UPGRADE_CLICKED, {
      feature,
      unlock_tier: tierLabel,
      variant,
    });
    history.push(BILLING_PATH);
  };

  if (variant === 'page') {
    return (
      <div
        className={`flex min-h-[60vh] flex-col items-center justify-center px-6 text-center ${className}`}
        data-entitlement-lock={feature}
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-inset">
          <Lock className="h-5 w-5 text-ink-muted" aria-hidden="true" />
        </div>
        <h2 className="mt-4 text-lg font-semibold text-ink">{heading}</h2>
        <p className="mt-1 max-w-sm text-sm text-ink-secondary">{body}</p>
        <button
          type="button"
          onClick={goToBilling}
          className="mt-5 rounded-md bg-ink px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          View plans
        </button>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-3 rounded-md border border-edge bg-paper-inset px-3 py-2.5 ${className}`}
      data-entitlement-lock={feature}
    >
      <Lock className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-ink">{heading}</p>
        <p className="text-[12px] text-ink-muted">{body}</p>
      </div>
      <button
        type="button"
        onClick={goToBilling}
        className="shrink-0 rounded-md border border-edge bg-white px-2.5 py-1 text-xs font-medium text-ink-secondary hover:bg-paper-inset"
      >
        View plans
      </button>
    </div>
  );
}

export default UpgradeLock;
